// RewardsTab — Phase 5 Task 13 port of docs/mockups/leaderboard-detail.html
// states starting at L1942 (default + preview), L2147 (empty), L2266 (inline
// drawer — adding), L2438 (inline drawer — editing), L2602 (Delete reward
// confirm modal).
//
// Layout: rewards table card on the left + a live "Reward preview" sidebar on
// the right that matches each current-top entry against the configured rewards
// using the same rules the backend uses when materializing grants (rank_range:
// rank ∈ [from..to]; percentile: entry's percentile rank ∈ [from..to]).
//
// Reward range fields are immutable per the current API
// (RewardUpdateParams accepts only {name?, payload?, position?}), so the
// edit-mode drawer only sends those three. Reordering via drag is OUT of scope
// for T13; the drag handle is rendered but inert. Users can change position via
// the inline editor.

import { useMemo, useState } from "react";
import { Menu } from "../../../components/Menu/Menu";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";

import { Alert } from "../../../components/Alert/Alert";
import { Modal } from "../../../components/Modal/Modal";
import { Button } from "../../../components/Button/Button";
import { Skeleton } from "../../../components/Skeleton/Skeleton";
import { ApiError, describeError } from "../../../lib/errors";
import {
  createReward,
  deleteReward,
  lbKeys,
  listCycleTop,
  listCycles,
  listRewards,
  updateReward,
} from "../api";
import type {
  CreateRewardInput,
  Cycle,
  Entry,
  Reward,
  UpdateRewardInput,
} from "../types";
import styles from "./RewardsTab.module.css";

// ──────────────────────────────────────────────────────────────────────────────
// Reward kind helpers — derive XOR'd kind from Reward.rank_from/percentile_from.
// (Reward has no explicit `kind` field; the DB enforces XOR via lb_rewards_
// range_xor.)
// ──────────────────────────────────────────────────────────────────────────────

type RewardKind = "rank_range" | "percentile";

function rewardKind(r: Pick<Reward, "rank_from" | "percentile_from">): RewardKind {
  if (r.percentile_from !== undefined && r.percentile_from !== null) {
    return "percentile";
  }
  return "rank_range";
}

function rewardRangeText(r: Reward): string {
  if (rewardKind(r) === "percentile") {
    return `${r.percentile_from} – ${r.percentile_to}%`;
  }
  return `${r.rank_from} – ${r.rank_to}`;
}

function rewardPayloadText(p: Reward["payload"]): string {
  try {
    return JSON.stringify(p ?? {});
  } catch {
    return "{}";
  }
}

/**
 * Chip-tint by reward category. The mockup (L309–311) uses three reward-chip
 * colors: default `accent-alpha`, `purple`, `amber`. We pick by kind so the
 * mapping is stable across re-renders and across previews of different
 * leaderboards:
 *   - percentile → amber (matches mockup's "Top 10%")
 *   - rank_range with rank_from === 1 → accent (matches "Champion")
 *   - rank_range otherwise → purple (matches "Top 10")
 */
function chipClassFor(r: Reward): string {
  if (rewardKind(r) === "percentile") return styles.chipAmber;
  if (r.rank_from === 1) return styles.chipAccent;
  return styles.chipPurple;
}

// ──────────────────────────────────────────────────────────────────────────────
// Preview matcher — same predicate the backend uses when grants are
// materialized on cycle-close. See internal/leaderboard/rewards.go.
//
// For a top entry of rank R out of N total entries:
//   percentile_rank = (1 - (R - 1) / N) * 100
// A reward with percentile_from=90, percentile_to=100 matches when
// percentile_rank ∈ [90, 100].
// ──────────────────────────────────────────────────────────────────────────────

function rewardMatchesEntry(
  r: Reward,
  entry: Entry,
  total: number,
): boolean {
  if (rewardKind(r) === "rank_range") {
    if (r.rank_from === undefined || r.rank_to === undefined) return false;
    return entry.rank >= r.rank_from && entry.rank <= r.rank_to;
  }
  if (r.percentile_from === undefined || r.percentile_to === undefined) {
    return false;
  }
  if (total <= 0) return false;
  const percentile = (1 - (entry.rank - 1) / total) * 100;
  return percentile >= r.percentile_from && percentile <= r.percentile_to;
}

function shortCycleId(id: string | undefined): string {
  if (!id) return "—";
  return id.replace(/-/g, "").slice(0, 12);
}

function describeQueryError(error: unknown): string {
  if (error instanceof ApiError) return describeError(error.code);
  return describeError("internal");
}

// ──────────────────────────────────────────────────────────────────────────────
// RewardsTab — entry point
// ──────────────────────────────────────────────────────────────────────────────

interface Props {
  gameId: string;
  lbId: string;
}

export function RewardsTab({ gameId, lbId }: Props) {
  const rewardsQ = useQuery({
    queryKey: lbKeys.rewards(gameId, lbId),
    queryFn: () => listRewards(gameId, lbId),
    enabled: !!gameId && !!lbId,
  });

  // Cycles → pick the currently-open one (or fall back to the first) so the
  // preview shows live grant projection against the active top.
  const cyclesQ = useQuery({
    queryKey: lbKeys.cycles(gameId, lbId),
    queryFn: () => listCycles(gameId, lbId),
    enabled: !!gameId && !!lbId,
  });
  const previewCycle: Cycle | undefined = useMemo(() => {
    const cs = cyclesQ.data?.cycles ?? [];
    return cs.find((c) => c.status === "open") ?? cs[0];
  }, [cyclesQ.data]);

  const topQ = useQuery({
    queryKey: lbKeys.top(gameId, lbId, previewCycle?.id ?? "", { limit: 10 }),
    queryFn: () => {
      if (!previewCycle) return Promise.reject(new Error("no cycle"));
      return listCycleTop(gameId, lbId, previewCycle.id, { limit: 10 });
    },
    enabled: !!previewCycle,
  });

  // Editor state: { kind: "add" } for the inline add-drawer; { kind: "edit",
  // id } when editing a row in-place.
  const [editor, setEditor] = useState<
    | { kind: "add" }
    | { kind: "edit"; reward: Reward }
    | null
  >(null);

  // Delete confirm modal — separate from the inline editor.
  const [deleteTarget, setDeleteTarget] = useState<Reward | null>(null);

  const rewards = rewardsQ.data ?? [];
  const sortedRewards = useMemo(
    () => [...rewards].sort((a, b) => a.position - b.position),
    [rewards],
  );
  const topEntries = topQ.data?.entries ?? [];

  // === Loading / error =============================================== //
  if (rewardsQ.isError) {
    return (
      <Alert variant="danger" title="Couldn't load rewards">
        {describeQueryError(rewardsQ.error)}
      </Alert>
    );
  }

  if (rewardsQ.isLoading) {
    return (
      <div className={styles.layout}>
        <div className={styles.card}>
          <Skeleton height={260} />
        </div>
        <aside className={styles.preview}>
          <Skeleton height={260} />
        </aside>
      </div>
    );
  }

  return (
    <div className={styles.layout}>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h3>Rewards</h3>
          <Button
            variant="primary"
            size="sm"
            disabled={editor !== null}
            onClick={() => setEditor({ kind: "add" })}
            icon={<Plus size={12} aria-hidden="true" />}
          >
            Add reward
          </Button>
        </div>

        {editor?.kind === "add" && (
          <RewardEditorRow
            mode="add"
            gameId={gameId}
            lbId={lbId}
            initial={{
              position: nextPosition(sortedRewards),
              name: "",
              kind: "rank_range",
              rangeFrom: "",
              rangeTo: "",
              payload: "",
            }}
            onClose={() => setEditor(null)}
          />
        )}
        {editor?.kind === "edit" && (
          <RewardEditorRow
            mode="edit"
            gameId={gameId}
            lbId={lbId}
            reward={editor.reward}
            initial={rewardToFormState(editor.reward)}
            onClose={() => setEditor(null)}
          />
        )}

        {sortedRewards.length === 0 && editor === null ? (
          <RewardsEmptyState onAdd={() => setEditor({ kind: "add" })} />
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th style={{ width: 56 }} className={styles.num}>
                  Pos.
                </th>
                <th style={{ width: 130 }}>Name</th>
                <th style={{ width: 110 }}>Type</th>
                <th style={{ width: 100 }}>Range</th>
                <th>Payload</th>
                <th className={styles.act} aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {sortedRewards
                .filter(
                  (r) =>
                    editor?.kind !== "edit" || editor.reward.id !== r.id,
                )
                .map((r) => (
                  <RewardRow
                    key={r.id}
                    reward={r}
                    onEdit={() => setEditor({ kind: "edit", reward: r })}
                    onDelete={() => setDeleteTarget(r)}
                  />
                ))}
            </tbody>
          </table>
        )}
      </div>

      <RewardPreviewSidebar
        editorState={editor}
        rewards={sortedRewards}
        topEntries={topEntries}
        cycle={previewCycle}
        topLoading={topQ.isLoading}
        topError={topQ.isError}
      />

      {deleteTarget && (
        <DeleteRewardModal
          gameId={gameId}
          lbId={lbId}
          reward={deleteTarget}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

function nextPosition(rewards: Reward[]): number {
  if (rewards.length === 0) return 1;
  return Math.max(...rewards.map((r) => r.position)) + 1;
}

// ──────────────────────────────────────────────────────────────────────────────
// RewardsEmptyState — mockup L2238–2248
// ──────────────────────────────────────────────────────────────────────────────

function RewardsEmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className={styles.emptyState}>
      <span className={styles.emptyIcon} aria-hidden="true">
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="20 12 20 22 4 22 4 12" />
          <rect x="2" y="7" width="20" height="5" />
          <line x1="12" y1="22" x2="12" y2="7" />
          <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
          <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
        </svg>
      </span>
      <h3>No rewards configured</h3>
      <p>Add rewards to materialize prizes when each cycle closes.</p>
      <Button
        variant="primary"
        onClick={onAdd}
        icon={<Plus size={12} aria-hidden="true" />}
      >
        Add reward
      </Button>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Per-row kebab menu
// ──────────────────────────────────────────────────────────────────────────────

function RewardRow({
  reward,
  onEdit,
  onDelete,
}: {
  reward: Reward;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const kind = rewardKind(reward);
  const badgeCls = kind === "rank_range" ? styles.badgeInfo : styles.badgeWarn;
  return (
    <tr>
      <td className={styles.num}>
        <span className={styles.posCell}>
          {/* TODO(reward-drag): wire actual drag-reorder once the order
              semantics are confirmed; manual position edit is sufficient for now */}
          <span className={styles.drag} aria-hidden="true" title="Drag to reorder">
            ≡
          </span>
          {reward.position}
        </span>
      </td>
      <td>
        <b>{reward.name}</b>
      </td>
      <td>
        <span className={`${styles.badge} ${badgeCls}`}>{kind}</span>
      </td>
      <td className={styles.monoCell}>{rewardRangeText(reward)}</td>
      <td>
        <span className={styles.payloadCell} title={rewardPayloadText(reward.payload)}>
          {rewardPayloadText(reward.payload)}
        </span>
      </td>
      <td className={styles.act}>
        <RowMenu onEdit={onEdit} onDelete={onDelete} />
      </td>
    </tr>
  );
}

function RowMenu({
  onEdit,
  onDelete,
}: {
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <Menu
      aria-label="Reward actions"
      placement="bottom-end"
      items={[
        { label: "Edit", onSelect: onEdit },
        { label: "Delete", variant: "danger", onSelect: onDelete },
      ]}
    />
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Inline editor row (used for both add and edit). Fields mirror mockup
// L2366–2380 (add) and L2538–2552 (edit). Range pair input is duplicated
// (from/to). For percentile, both are 0–100 and rendered with a "%" suffix.
// ──────────────────────────────────────────────────────────────────────────────

interface RewardFormState {
  position: number;
  name: string;
  kind: RewardKind;
  rangeFrom: string;
  rangeTo: string;
  payload: string;
}

function rewardToFormState(r: Reward): RewardFormState {
  const k = rewardKind(r);
  return {
    position: r.position,
    name: r.name,
    kind: k,
    rangeFrom:
      k === "percentile"
        ? String(r.percentile_from ?? "")
        : String(r.rank_from ?? ""),
    rangeTo:
      k === "percentile"
        ? String(r.percentile_to ?? "")
        : String(r.rank_to ?? ""),
    payload:
      r.payload && Object.keys(r.payload).length > 0
        ? JSON.stringify(r.payload)
        : "",
  };
}

function RewardEditorRow(
  props:
    | {
        mode: "add";
        gameId: string;
        lbId: string;
        initial: RewardFormState;
        onClose: () => void;
      }
    | {
        mode: "edit";
        gameId: string;
        lbId: string;
        reward: Reward;
        initial: RewardFormState;
        onClose: () => void;
      },
) {
  const qc = useQueryClient();
  const [form, setForm] = useState<RewardFormState>(props.initial);
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: (input: CreateRewardInput) =>
      createReward(props.gameId, props.lbId, input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: lbKeys.rewards(props.gameId, props.lbId),
      });
      props.onClose();
    },
    onError: (err) => {
      setError(
        err instanceof ApiError ? describeError(err.code) : describeError("internal"),
      );
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ rid, input }: { rid: string; input: UpdateRewardInput }) =>
      updateReward(props.gameId, props.lbId, rid, input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: lbKeys.rewards(props.gameId, props.lbId),
      });
      props.onClose();
    },
    onError: (err) => {
      setError(
        err instanceof ApiError ? describeError(err.code) : describeError("internal"),
      );
    },
  });

  const isPending = createMut.isPending || updateMut.isPending;

  function parsePayload(): Record<string, unknown> | undefined | "ERR" {
    const trimmed = form.payload.trim();
    if (!trimmed) return undefined;
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
      return "ERR";
    } catch {
      return "ERR";
    }
  }

  function onSave() {
    setError(null);
    const name = form.name.trim();
    if (!name) {
      setError("Name is required.");
      return;
    }
    const pos = Number(form.position);
    if (!Number.isFinite(pos) || !Number.isInteger(pos) || pos < 1) {
      setError("Position must be a positive integer.");
      return;
    }
    const payload = parsePayload();
    if (payload === "ERR") {
      setError("Payload must be a valid JSON object.");
      return;
    }

    if (props.mode === "edit") {
      // Range fields are immutable per UpdateRewardInput contract.
      const input: UpdateRewardInput = {
        name,
        position: pos,
        payload,
      };
      updateMut.mutate({ rid: props.reward.id, input });
      return;
    }

    const from = Number(form.rangeFrom);
    const to = Number(form.rangeTo);
    if (!Number.isFinite(from) || !Number.isFinite(to)) {
      setError("Range bounds must be numbers.");
      return;
    }
    if (form.kind === "percentile" && (from < 0 || to > 100)) {
      setError("Percentile bounds must be within 0–100.");
      return;
    }
    if (from > to) {
      setError("Range start must be ≤ end.");
      return;
    }

    const input: CreateRewardInput = {
      name,
      position: pos,
      payload,
    };
    if (form.kind === "rank_range") {
      input.rank_from = from;
      input.rank_to = to;
    } else {
      input.percentile_from = from;
      input.percentile_to = to;
    }
    createMut.mutate(input);
  }

  const percentSuffix = form.kind === "percentile" ? "%" : "";

  return (
    <div className={styles.editRow}>
      <input
        type="number"
        inputMode="numeric"
        className={`${styles.input} ${styles.inputMono}`}
        value={form.position}
        onChange={(e) =>
          setForm((f) => ({ ...f, position: Number(e.target.value) }))
        }
        placeholder="Pos."
        title="position"
        aria-label="Position"
      />
      <input
        type="text"
        className={styles.input}
        value={form.name}
        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        placeholder="Name"
        aria-label="Name"
      />
      <div
        role="radiogroup"
        aria-label="Type"
        className={styles.seg}
      >
        <button
          type="button"
          role="radio"
          aria-checked={form.kind === "rank_range"}
          className={`${styles.segOpt} ${form.kind === "rank_range" ? styles.segOptActive : ""}`}
          disabled={props.mode === "edit"}
          onClick={() => setForm((f) => ({ ...f, kind: "rank_range" }))}
        >
          rank_range
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={form.kind === "percentile"}
          className={`${styles.segOpt} ${form.kind === "percentile" ? styles.segOptActive : ""}`}
          disabled={props.mode === "edit"}
          onClick={() => setForm((f) => ({ ...f, kind: "percentile" }))}
        >
          percentile
        </button>
      </div>
      <div className={styles.rangePair}>
        <input
          type="number"
          inputMode="numeric"
          className={`${styles.input} ${styles.inputMono} ${styles.inputRange}`}
          value={form.rangeFrom}
          onChange={(e) =>
            setForm((f) => ({ ...f, rangeFrom: e.target.value }))
          }
          disabled={props.mode === "edit"}
          aria-label="Range from"
        />
        {percentSuffix && (
          <span className={styles.percentSuffix} aria-hidden="true">
            %
          </span>
        )}
        <span className={styles.dash} aria-hidden="true">
          –
        </span>
        <input
          type="number"
          inputMode="numeric"
          className={`${styles.input} ${styles.inputMono} ${styles.inputRange}`}
          value={form.rangeTo}
          onChange={(e) => setForm((f) => ({ ...f, rangeTo: e.target.value }))}
          disabled={props.mode === "edit"}
          aria-label="Range to"
        />
        {percentSuffix && (
          <span className={styles.percentSuffix} aria-hidden="true">
            %
          </span>
        )}
      </div>
      <textarea
        className={`${styles.input} ${styles.inputMono} ${styles.editPayload}`}
        value={form.payload}
        onChange={(e) => setForm((f) => ({ ...f, payload: e.target.value }))}
        placeholder='{"gold":250,"gems":10}'
        aria-label="Payload"
      />
      <div className={styles.editActions}>
        <Button
          variant="ghost"
          size="sm"
          onClick={props.onClose}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={onSave}
          loading={isPending}
        >
          Save
        </Button>
      </div>
      {error && (
        <div className={styles.editError} role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Reward preview sidebar — mockup L2083–2138 / L2421–2429 / L2585–2593.
// ──────────────────────────────────────────────────────────────────────────────

function RewardPreviewSidebar({
  editorState,
  rewards,
  topEntries,
  cycle,
  topLoading,
  topError,
}: {
  editorState:
    | { kind: "add" }
    | { kind: "edit"; reward: Reward }
    | null;
  rewards: Reward[];
  topEntries: Entry[];
  cycle: Cycle | undefined;
  topLoading: boolean;
  topError: boolean;
}) {
  let sub: React.ReactNode;
  if (editorState?.kind === "add") {
    sub = "Live preview — updates when you save";
  } else if (editorState?.kind === "edit") {
    sub = "Live preview — updates when you save";
  } else if (rewards.length === 0) {
    sub = "No rewards configured — preview empty.";
  } else if (cycle) {
    sub = (
      <>
        Based on current cycle top — <code>{shortCycleId(cycle.id)}</code>
      </>
    );
  } else {
    sub = "No active cycle to preview against.";
  }

  // Body content
  let body: React.ReactNode = null;
  if (editorState?.kind === "add") {
    body = (
      <div className={styles.previewMessage}>
        Add the new reward to see its effect on the current top.
      </div>
    );
  } else if (editorState?.kind === "edit") {
    body = (
      <div className={styles.previewMessage}>
        Editing <b>{editorState.reward.name}</b> — preview will refresh on save.
      </div>
    );
  } else if (rewards.length === 0) {
    body = (
      <div className={styles.previewMessage}>
        Add a reward to see who would receive it.
      </div>
    );
  } else if (topError) {
    body = (
      <div className={styles.previewMessage}>
        Couldn&apos;t load top entries for preview.
      </div>
    );
  } else if (topLoading) {
    body = (
      <div className={styles.previewMessage}>
        <Skeleton height={120} />
      </div>
    );
  } else if (topEntries.length === 0) {
    body = (
      <div className={styles.previewMessage}>
        No entries yet — preview will populate as players submit scores.
      </div>
    );
  } else {
    const total = topEntries.length;
    body = topEntries.map((e) => {
      const matched = rewards.filter((r) => rewardMatchesEntry(r, e, total));
      return (
        <div key={e.player.id} className={styles.prevRow}>
          <span className={styles.rk}>{e.rank}</span>
          <span className={styles.nm}>{e.player.display_name}</span>
          <div className={styles.chips}>
            {matched.map((r) => (
              <span
                key={r.id}
                className={`${styles.rewardChip} ${chipClassFor(r)}`}
              >
                {r.name}
              </span>
            ))}
          </div>
        </div>
      );
    });
  }

  return (
    <aside className={styles.preview}>
      <div className={styles.previewHead}>
        <h3>Reward preview</h3>
        <div className={styles.previewSub}>{sub}</div>
      </div>
      {body}
    </aside>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Delete confirm modal — mockup L2752–2769.
// ──────────────────────────────────────────────────────────────────────────────

function DeleteRewardModal({
  gameId,
  lbId,
  reward,
  onClose,
}: {
  gameId: string;
  lbId: string;
  reward: Reward;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => deleteReward(gameId, lbId, reward.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: lbKeys.rewards(gameId, lbId) });
      onClose();
    },
    onError: (err) => {
      setError(
        err instanceof ApiError ? describeError(err.code) : describeError("internal"),
      );
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`Delete reward ${reward.name}?`}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => mutation.mutate()}
            loading={mutation.isPending}
          >
            Delete
          </Button>
        </>
      }
    >
      <p className={styles.modalBody}>
        This will remove the reward template. Already-materialized grants in
        past cycles are not affected.
      </p>
      {error && (
        <div className={styles.editError} role="alert">
          {error}
        </div>
      )}
    </Modal>
  );
}
